const TONAPI_RATES_URL = "https://tonapi.io/v2/rates?tokens=ton&currencies=usd";
const CACHE_TTL_MS = 5 * 60 * 1000;
const REQUEST_TIMEOUT_MS = 5000;

type TonApiRatesResponse = {
  rates?: {
    TON?: {
      prices?: {
        USD?: unknown;
      };
    };
  };
};

let cachedRate: { rate: number; ts: number } | null = null;
let inFlight: Promise<number | null> | null = null;

async function fetchTonUsdRate(): Promise<number | null> {
  const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timeoutId = setTimeout(() => controller?.abort(), REQUEST_TIMEOUT_MS);

  try {
    const response = await fetch(TONAPI_RATES_URL, { signal: controller?.signal });
    if (!response.ok) return null;

    const json = (await response.json()) as TonApiRatesResponse;
    const rate = json?.rates?.TON?.prices?.USD;
    if (typeof rate !== "number" || !Number.isFinite(rate) || rate <= 0) return null;

    return rate;
  } catch {
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Current TON/USD rate from TonAPI, cached for 5 minutes.
 * Used for analytics only, never for payments. Resolves to null when the rate is unavailable.
 */
export function getTonUsdRate(): Promise<number | null> {
  if (cachedRate && Date.now() - cachedRate.ts < CACHE_TTL_MS) {
    return Promise.resolve(cachedRate.rate);
  }

  if (inFlight) return inFlight;

  const request = fetchTonUsdRate().then((rate) => {
    if (rate !== null) cachedRate = { rate, ts: Date.now() };
    if (inFlight === request) inFlight = null;
    return rate;
  });

  inFlight = request;
  return request;
}

/** Test helper: resets module state between test cases. */
export function resetTonUsdRateForTests() {
  cachedRate = null;
  inFlight = null;
}
